// [S-617] Precedent Analytics (structured measures store) hooks.
//
// Backed by apps/admin/src/routes/precedentAnalytics.ts:
//   GET  /precedent-analytics/config    (current toggle status)
//   POST /precedent-analytics/enable    (provision the tenant's schema)
//   POST /precedent-analytics/disable   (flip off — never drops the schema)
//
// enable/disable return 501 not_implemented today — no measures Postgres
// instance is provisioned anywhere yet. The UI catches that and shows an
// honest "Pending backend: S-617" state, no fake success.
//
// Named "Precedent Analytics", not "Analytics" — this app already has an
// unrelated Analytics dashboard at /analytics (usage stats, E-ANALYTICS).
// Don't conflate the two.
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { useApi } from "./api";

export type PrecedentAnalyticsStatus = "disabled" | "enabling" | "active" | "failed";

export type PrecedentAnalyticsConfig = {
  status: PrecedentAnalyticsStatus;
  /** False → this backend has no measures-store credentials, so the toggle
   *  cannot work no matter what the admin does. Distinct from status
   *  "disabled", which means the feature is simply off. Without the
   *  distinction the toggle appeared to flip and snap back with no reason
   *  given, which read as a broken switch rather than a deployment gap. */
  configured?: boolean;
  schema?: string;
  windowMonths?: number;
  lastError?: string;
};

export function usePrecedentAnalyticsConfig(
  tenantId: string
): UseQueryResult<PrecedentAnalyticsConfig, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["precedent-analytics-config", tenantId],
    queryFn: () => api<PrecedentAnalyticsConfig>("/precedent-analytics/config"),
  });
}

export function useEnablePrecedentAnalytics(
  tenantId: string
): UseMutationResult<PrecedentAnalyticsConfig, Error, { windowMonths?: number } | void> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args) =>
      api<PrecedentAnalyticsConfig>("/precedent-analytics/enable", {
        method: "POST",
        body: { windowMonths: args?.windowMonths },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["precedent-analytics-config", tenantId],
      });
    },
  });
}

export function useDisablePrecedentAnalytics(
  tenantId: string
): UseMutationResult<PrecedentAnalyticsConfig, Error, void> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<PrecedentAnalyticsConfig>("/precedent-analytics/disable", { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["precedent-analytics-config", tenantId],
      });
    },
  });
}

export type BackfillRunStatus = "running" | "completed" | "failed";

export type BackfillRun = {
  runId: string;
  status: BackfillRunStatus;
  windowMonths: number;
  startedBy: string;
  totalCandidates: number;
  processed: number;
  written: number;
  skippedNoSourceFile: number;
  skippedNoCompensation: number;
  /** Already up to date from a previous run — not re-derived. Optional: runs
   *  recorded before this existed omit it. */
  skippedUnchanged?: number;
  startedAt: string;
  completedAt?: string;
  error?: string;
};

/** "This will scan ~N precedents from the last W months" — shown before the
 *  admin commits to populating. 501s the same way enable/disable do until
 *  there's a real measures store; the caller shows the honest pending state. */
export function useBackfillEstimate(
  windowMonths: number,
  enabled: boolean
): UseQueryResult<{ count: number }, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["precedent-analytics-backfill-estimate", windowMonths],
    queryFn: () =>
      api<{ count: number }>(`/precedent-analytics/backfill-estimate?windowMonths=${windowMonths}`),
    enabled,
  });
}

export function useStartBackfill(
  tenantId: string
): UseMutationResult<BackfillRun, Error, { windowMonths: number; force?: boolean }> {
  const api = useApi("admin");
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ windowMonths, force }) =>
      api<BackfillRun>("/precedent-analytics/backfill", {
        method: "POST",
        // Only sent when true — an explicit `force: false` would read as a
        // deliberate choice in the request log rather than the default path.
        body: force === true ? { windowMonths, force: true } : { windowMonths },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["precedent-analytics-backfill-status", tenantId],
      });
    },
  });
}

/** Polls every 2s while the latest run is 'running'; stops once it settles. */
export function useBackfillStatus(
  tenantId: string,
  enabled: boolean
): UseQueryResult<BackfillRun | null, Error> {
  const api = useApi("admin");
  return useQuery({
    queryKey: ["precedent-analytics-backfill-status", tenantId],
    queryFn: () => api<BackfillRun | null>("/precedent-analytics/backfill-status"),
    enabled,
    refetchInterval: (query) => (query.state.data?.status === "running" ? 2000 : false),
  });
}
